import React, { useState, useEffect, Fragment, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import { toast } from "react-toastify";
import Pagination from "react-pagination-js";
import swal from "sweetalert";
import Select from "react-select";

// ── Status → API param map ──────────────────────────────────────────────────
const TAB_STATUS_MAP = {
  pending: "Pending",
  negotiation: "negotiate",
  rejected: "rejected",
  synced: "Accepted",
  closed: "closed",
};

const normalizeEstimate = (est) => {
  console.log("Normalizing estimate:", est);
  const isQB = est.type === "quickbookEstimate";
  const raw = est.raw || {};

  // Status handling
  let rawStatus =
    est.status || raw.TxnStatus || est.mainRequest?.status || "PENDING";
  let status = String(rawStatus).toUpperCase();
  if (status === "ESTIMATED" || status === "ESTIMATE") {
    status = "PENDING";
  } else if (status === "NEGOTIATION") {
    status = "NEGOTIATE";
  }
  // Convert standard statuses
  if (status === "SYNCED") status = "ACCEPTED";
  if (status === "CONVERTED") status = "CLOSED";

  // Identifiers
  const id = est._id;
  const qbId = est.qbId || raw.Id || null;
  const docNumber =
    est.docNumber ||
    est.doc_number ||
    est.estimate_no ||
    raw.DocNumber ||
    `EST-${id?.slice(-6)}`;

  // Customer info
  const customerName =
    est.customerName ||
    est.customer_name ||
    raw.CustomerRef?.name ||
    est.displayName ||
    "—";
  const email = est.email || raw.BillEmail?.Address || est.displayEmail || "—";

  // Dates
  const txnDate =
    est.txnDate || est.txn_date || raw.TxnDate || est.createdAt || null;
  const expirationDate = est.expiration_date || raw.ExpirationDate || null;

  // Currency
  const currencyCode = raw.CurrencyRef?.value || "USD";

  // Addresses
  const billAddr =
    raw.BillAddr ||
    (est.billingAddress
      ? {
          Line1: est.billingAddress.bill_line1,
          Line2: est.billingAddress.bill_line2,
          City: est.billingAddress.bill_city,
          Country: est.billingAddress.bill_country,
          PostalCode: est.billingAddress.bill_postal_code,
        }
      : est.originalQuoteData?.BillAddr
        ? {
            Line1: est.originalQuoteData.BillAddr.Line1,
            Line2: est.originalQuoteData.BillAddr.Line2,
            City: est.originalQuoteData.BillAddr.City,
            Country: est.originalQuoteData.BillAddr.Country,
            PostalCode: est.originalQuoteData.BillAddr.PostalCode,
          }
        : null);

  const shipAddr =
    raw.ShipAddr ||
    (est.shippingAddress
      ? {
          Line1: est.shippingAddress.ship_line1,
          Line2: est.shippingAddress.ship_line2,
          City: est.shippingAddress.ship_city,
          Country: est.shippingAddress.ship_country,
          PostalCode: est.shippingAddress.ship_postal_code,
        }
      : est.originalQuoteData?.ShipAddr
        ? {
            Line1: est.originalQuoteData.ShipAddr.Line1,
            Line2: est.originalQuoteData.ShipAddr.Line2,
            City: est.originalQuoteData.ShipAddr.City,
            Country: est.originalQuoteData.ShipAddr.Country,
            PostalCode: est.originalQuoteData.ShipAddr.PostalCode,
          }
        : null);

  // Line Items
  let rawLines = est?.customer_line_items || raw.Line;
  if (!rawLines || rawLines.length === 0) {
    rawLines =
      est.line_items && est.line_items.length > 0
        ? est.line_items
        : est.product_line_items;
  }
  if (!rawLines || rawLines.length === 0) {
    rawLines = est.originalQuoteData?.Line || [];
  }
  const lineItems = rawLines
    .filter(
      (l) =>
        l.DetailType === "SalesItemLineDetail" ||
        l.item_name ||
        l.name ||
        l.productName ||
        l.Description ||
        l.description,
    )
    .map((line, index) => {
      let itemName = "—",
        quantity = 1,
        unitPrice = 0,
        amount = 0,
        description = "";
      let productId = "";

      if (
        line.DetailType === "SalesItemLineDetail" &&
        line.SalesItemLineDetail
      ) {
        itemName =
          line.SalesItemLineDetail.ItemRef?.name || line.Description || "—";
        quantity = Number(line.SalesItemLineDetail.Qty || line.qty || 1);
        unitPrice = Number(
          line.SalesItemLineDetail.UnitPrice || line.rate || 0,
        );
        amount = Number(line.Amount || line.amount || quantity * unitPrice);
        description = line.Description || "";
        productId =
          line.SalesItemLineDetail.ItemRef?.value || line.db_product_id || "";
      } else if (line.item_name || line.name || line.productName) {
        itemName = line.item_name || line.name || line.productName;
        quantity = Number(line.quantity || line.qty || 1);
        unitPrice = Number(line.price || line.unitPrice || line.rate || 0);
        amount = Number(line.amount || line.total || quantity * unitPrice);
        description = line.description || line.Description || "";
        productId = line.productId || line.item_name || "";
      }

      return {
        id: line.Id || `line-${index}`,
        productId,
        itemName,
        description,
        quantity,
        unitPrice,
        amount,
      };
    });

  // Calculate Subtotal (Sum of line items amount)
  const calculatedSubtotal = lineItems.reduce(
    (acc, item) => acc + (item.amount || 0),
    0,
  );

  // Tax
  const tax = Number(
    est.totalTax ?? est.total_tax ?? raw.TxnTaxDetail?.TotalTax ?? 0,
  );

  // Discount
  let discountType = est.discount_type || null;
  let discountValue = est.discount_value || 0;
  let calculatedDiscount = 0;

  if (isQB) {
    const discountLine =
      rawLines.find((l) => l.DetailType === "DiscountLineDetail") || null;
    if (discountLine) {
      discountValue = Number(discountLine.Amount || 0);
      if (discountLine.DiscountLineDetail?.PercentBased) {
        discountType = "percent";
        discountValue = Number(
          discountLine.DiscountLineDetail.DiscountPercent || 0,
        );
      }
    }
  }
  if (discountType === "percent") {
    calculatedDiscount = (calculatedSubtotal * discountValue) / 100;
  } else {
    calculatedDiscount = discountValue;
  }

  // Grand Total
  // Using explicit est values if they exist, otherwise calculated
  const total = Number(
    est.totalAmount ??
      est.total_amount ??
      raw.TotalAmt ??
      calculatedSubtotal + tax - calculatedDiscount,
  );

  // Extra metadata
  const customFields = raw.CustomField || est.CustomField || [];
  let displayDocNumber = docNumber;
  const topsField = customFields.find(
    (f) => f.Name === "Tops Shipping Systems, Corp",
  );
  if (
    topsField &&
    topsField.StringValue &&
    topsField.StringValue !== "ORD - null"
  ) {
    displayDocNumber = topsField.StringValue.replace("ORD - ", "");
  }

  const poNumber =
    est.po_number ||
    raw.PONumber ||
    customFields.find((f) => f.DefinitionId === "3")?.StringValue ||
    "—";

  const customerMemo = est.customer_memo || raw.CustomerMemo?.value || null;
  const privateNote = est.private_note || raw.PrivateNote || null;
  const negotiateMessage =
    est.negotiateMessage ||
    est.negotiation_message ||
    raw.negotiateMessage ||
    null;

  return {
    id,
    qbId,
    type: est.type || "unknown",
    isQB,
    docNumber,
    displayDocNumber,
    customerName,
    email,
    status,
    txnDate,
    expirationDate,
    currencyCode,
    billAddr,
    shipAddr,
    lineItems,
    subtotal: calculatedSubtotal,
    tax,
    discountType,
    discountValue,
    discountAmt: calculatedDiscount,
    markupDetails: est?.markupDetails?.customer_subtotal
      ? est?.markupDetails
      : {
          ...est?.markup,
          customer_subtotal:
            parseFloat(calculatedSubtotal) + est?.markup?.amount,
        },
    total,
    poNumber,
    customerMemo,
    privateNote,
    negotiateMessage,
    requestedProducts: est.requested_products || [],
    mainRequestId: est.mainRequest?._id || raw.Id || id,
    originalData: est,
    domain: raw.domain || "QBO",
    taxDetail: raw.TxnTaxDetail || (tax ? { TotalTax: tax } : null),
    isPending: ["PENDING", "NEGOTIATE", "REJECTED"].includes(status),
  };
};

const PaginationTextView = (currentPage, perPage, total) => {
  const start = (currentPage - 1) * perPage + 1;
  const end = Math.min(currentPage * perPage, total);
  return `Showing ${start} to ${end} of ${total} entries`;
};

export default function QuickBookEstimateList({
  quotesToken,
  quotesCompanyId,
  subTab,
  metaId,
  triggerDate,
}) {
  const user = useSelector((state) => state.user);
  const history = useNavigate();
  const isConnected = quotesToken || !!user?.quick_book_accesToken;

  // ── Tab / search / filter state ──────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(subTab || "pending");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // ── Data state ───────────────────────────────────────────────────────────
  const [estimates, setEstimates] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ page: 1, count: 10 });

  // ── Tab counts ────────────────────────────────────────────────────────────
  const [tabCounts, setTabCounts] = useState({
    pending: 0,
    negotiate: 0,
    rejected: 0,
    synced: 0,
    closed: 0,
  });

  // ── Modal / action state ─────────────────────────────────────────────────
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [isPoNumberOpen, setIsPoNumberOpen] = useState(false);
  const [poNumber, setPoNumber] = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  const [isNegotiationModalOpen, setIsNegotiationModalOpen] = useState(false);
  const [negotiationData, setNegotiationData] = useState({
    products: [],
    message: "",
    selectedProducts: [],
  });
  const [availableProducts, setAvailableProducts] = useState([]);

  // ── NEW unified fetch ────────────────────────────────────────────────────
  /**
   * Fetches estimates from the new unified endpoint.
   * @param {object} overrides – optional overrides for page / count / status / search
   */
  const fetchEstimates = useCallback(
    async (overrides = {}) => {
      if (!isConnected) return;
      loader(true);

      const tab = overrides.tab ?? activeTab;
      const page = overrides.page ?? filters.page;
      const count = overrides.count ?? filters.count;
      const search = overrides.search ?? searchQuery;
      const status = TAB_STATUS_MAP[tab] || "negotiate";

      try {
        const params = {
          page,
          limit: count,
          status,
          email: user?.email,
        };
        if (search) params.search = search;

        const res = await ApiClient.get(
          "quickbooks-docs/estimates/list",
          params,
        );

        if (res?.success) {
          const rawList = Array.isArray(res.data) ? res.data : [];
          const normalized = rawList.map(normalizeEstimate);

          setEstimates(normalized);
          // setEstimates(res.data || []);
          setTotal(res.pagination?.total || 0);
        } else {
          setEstimates([]);
          setTotal(0);
        }
      } catch (err) {
        console.error("Error fetching estimates:", err);
        setEstimates([]);
        setTotal(0);
      } finally {
        loader(false);
      }
    },
    [isConnected, activeTab, filters, searchQuery, user],
  );

  // ── Fetch all tab counts ─────────────────────────────────────────────────
  const fetchAllCounts = useCallback(async () => {
    if (!isConnected) return;
    try {
      const [pendingRes, negotiationRes, rejectedRes, syncedRes, closedRes] =
        await Promise.allSettled([
          ApiClient.get("quickbooks-docs/estimates/list", {
            page: 1,
            limit: 1,
            status: "Pending",
            email: user?.email,
          }),
          ApiClient.get("quickbooks-docs/estimates/list", {
            page: 1,
            limit: 1,
            status: "negotiate",
            email: user?.email,
          }),
          ApiClient.get("quickbooks-docs/estimates/list", {
            page: 1,
            limit: 1,
            status: "rejected",
            email: user?.email,
          }),
          ApiClient.get("quickbooks-docs/estimates/list", {
            page: 1,
            limit: 1,
            status: "Accepted",
            email: user?.email,
          }),
          ApiClient.get("quickbooks-docs/estimates/list", {
            page: 1,
            limit: 1,
            status: "closed",
            email: user?.email,
          }),
        ]);

      setTabCounts({
        pending:
          pendingRes.status === "fulfilled" && pendingRes.value?.success
            ? pendingRes.value?.pagination?.total || 0
            : 0,
        negotiate:
          negotiationRes.status === "fulfilled" && negotiationRes.value?.success
            ? negotiationRes.value?.pagination?.total || 0
            : 0,
        rejected:
          rejectedRes.status === "fulfilled" && rejectedRes.value?.success
            ? rejectedRes.value?.pagination?.total || 0
            : 0,
        synced:
          syncedRes.status === "fulfilled" && syncedRes.value?.success
            ? syncedRes.value?.pagination?.total || 0
            : 0,
        closed:
          closedRes.status === "fulfilled" && closedRes.value?.success
            ? closedRes.value?.pagination?.total || 0
            : 0,
      });
    } catch (error) {
      console.error("Error fetching counts:", error);
    }
  }, [isConnected, user]);

  // ── Effects ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isConnected) fetchAllCounts();
  }, [isConnected]);

  useEffect(() => {
    if (subTab) setActiveTab(subTab);
  }, [subTab]);

  // Re-fetch whenever tab, page, or count changes
  useEffect(() => {
    if (isConnected) fetchEstimates();
  }, [activeTab, filters.page, filters.count, isConnected]);

  // ── Tab switch helper ────────────────────────────────────────────────────
  const switchTab = (tab) => {
    setActiveTab(tab);
    setStatusFilter("");
    setSearchQuery("");
    setFilters({ page: 1, count: 10 });
    fetchAllCounts();
    // fetchEstimates is triggered by the useEffect above
  };

  // ── Search (debounce via button or Enter) ────────────────────────────────
  const handleSearch = () => {
    setFilters((f) => ({ ...f, page: 1 }));
    fetchEstimates({ page: 1, search: searchQuery });
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  // ── Pagination ───────────────────────────────────────────────────────────
  const handlePageChange = (page) => {
    setFilters((f) => ({ ...f, page }));
    // fetchEstimates is triggered by the useEffect above
  };

  // ── Derived stats from current page ──────────────────────────────────────
  const totalAmt = estimates.reduce((s, e) => s + (e.total || 0), 0);
  const totalTaxAmt = estimates.reduce((s, e) => s + (e.tax || 0), 0);

  // ── Client-side status filter (secondary filter on top of API results) ───
  const filteredEstimates = estimates.filter((est) => {
    const matchesStatus = !statusFilter || est.status === statusFilter;
    return matchesStatus;
  });

  const uniqueStatuses = [
    ...new Set(estimates.map((e) => e.status).filter(Boolean)),
  ];

  // ── Fetch available products from QuickBooks ──────────────────────────────
  const fetchAvailableProducts = async () => {
    loader(true);
    try {
      const response = await ApiClient.post("quickbook/items/db", {
        quickbook_token: user?.quick_book_accesToken || quotesToken,
        company: user?.quick_book_companyId || quotesCompanyId,
      });
      if (response?.success) {
        setAvailableProducts(
          response.data?.map((item) => ({
            ...item,
            Name: item?.name,
            Description: item?.description,
            Id: item?.qbId,
            UnitPrice: item?.unitPrice,
          })) || [],
        );
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      loader(false);
    }
  };

  // ── Send counter offer ────────────────────────────────────────────────────
  const sendCounterOffer = async () => {
    if (!selectedEstimate) return;

    setActionLoading(selectedEstimate?.id);
    loader(true);

    const hasInvalidProduct = negotiationData.selectedProducts.some(
      (p) =>
        ((p.productId && p.productId !== "") ||
          (p.productName && p.productName !== "")) &&
        (Number(p.quantity) <= 0 || Number(p.unitPrice) <= 0),
    );

    if (hasInvalidProduct) {
      toast.error("Quantity and Unit Price must be greater than 0");
      return;
    }

    const validProducts = negotiationData.selectedProducts.filter(
      (p) =>
        (p.productId && p.productId !== "") ||
        (p.productName && p.productName !== ""),
    );

    if (validProducts.length === 0) {
      toast.error("Please select at least one product for negotiation");
      return;
    }

    setActionLoading(selectedEstimate.id);
    loader(true);

    try {
      const totalAmount = validProducts.reduce(
        (sum, p) => sum + Number(p.quantity) * Number(p.unitPrice),
        0,
      );
      const taxPercent = Number(selectedEstimate?.originalData?.tax_percent);
      const totalTaxAmt = (totalAmount * taxPercent) / 100;

      const counterOfferPayload = {
        id: selectedEstimate.id,
        discount_value: selectedEstimate?.discountValue,
        discount_type: selectedEstimate?.discountType,
        total_tax: totalTaxAmt,
        negotiationMessage: negotiationData.message,
        status: "negotiate",
        markup_value: 0,
        line_items: validProducts.map((p) => ({
          productId: p.productId,
          item_name: p.productName,
          qty: p.quantity,
          rate: p.unitPrice,
          description: p.description,
        })),
      };

      const response = await ApiClient.put(
        "generate-quote/update",
        counterOfferPayload,
      );

      if (response?.success) {
        setIsNegotiationModalOpen(false);
        setNegotiationData({ products: [], message: "", selectedProducts: [] });
        await fetchAllCounts();
        fetchEstimates();
        swal({
          icon: "success",
          title: "Counter Offer",
          text: "Counter offer sent successfully and moved to the Negotiation tab. Click below to view the details.",
          buttons: {
            cancel: {
              text: "Close",
              value: false,
              visible: true,
            },
            confirm: {
              text: "View Details",
              value: true,
            },
          },
        }).then((viewDetails) => {
          if (viewDetails) {
            history(
              `/estimate/detail/${response?.data?._id}?date=${Date.now()}`,
            );
          }
        });
      } else {
        toast.error(response?.message || "Failed to send counter offer");
      }
    } catch (error) {
      console.error("Error sending counter offer:", error);
      toast.error("Error sending counter offer. Please try again.");
    } finally {
      loader(false);
      setActionLoading(null);
    }
  };

  // ── Negotiation product helpers ───────────────────────────────────────────
  const addNegotiationProduct = () => {
    setNegotiationData((prev) => ({
      ...prev,
      selectedProducts: [
        ...prev.selectedProducts,
        {
          id: Date.now(),
          productId: "",
          productName: "",
          quantity: 1,
          unitPrice: 0,
          description: "",
        },
      ],
    }));
  };

  const updateNegotiationProduct = (index, field, value) => {
    const updatedProducts = [...negotiationData.selectedProducts];

    if (field === "productId") {
      const selectedProduct = availableProducts.find(
        (p) =>
          p.Id === value ||
          p.Name?.toLowerCase() === value?.toLowerCase() ||
          p.Id?.toString() === value?.toString(),
      );
      if (selectedProduct) {
        updatedProducts[index].productId = selectedProduct.Id;
        updatedProducts[index].productName = selectedProduct.Name;
        updatedProducts[index].unitPrice = selectedProduct.UnitPrice || 0;
        updatedProducts[index].description = selectedProduct.Description || "";
      } else if (value && typeof value === "string") {
        updatedProducts[index].productId = value;
        updatedProducts[index].productName = value;
      }
    } else if (field === "quantity") {
      updatedProducts[index].quantity = value === "" ? "" : parseFloat(value);
    } else if (field === "unitPrice") {
      updatedProducts[index].unitPrice = value === "" ? "" : parseFloat(value);
    } else {
      updatedProducts[index][field] = value;
    }

    setNegotiationData((prev) => ({
      ...prev,
      selectedProducts: updatedProducts,
    }));
  };

  const removeNegotiationProduct = (index) => {
    setNegotiationData((prev) => ({
      ...prev,
      selectedProducts: prev.selectedProducts.filter((_, i) => i !== index),
    }));
  };

  // ── Accept / Reject quote ─────────────────────────────────────────────────
  const updateQuoteStatus = async (quoteId, status, quoteData = {}, type) => {
    swal({
      title: `Are you sure you want to ${status} this quote?`,
      text: "This action cannot be undone.",
      icon: "warning",
      buttons: ["Cancel", "Yes"],
      dangerMode: status === "reject",
    }).then(async (willConfirm) => {
      if (willConfirm) {
        loader(true);
        setActionLoading(quoteId);
        const payload = { id: quoteId, status, po_number: quoteData?.poNumber };
        let url = "request-quote/customer/status/change";
        if (type == "quickbookEstimate") {
          url = "quickbook/update/estimate/status";
        }
        try {
          const response = await ApiClient.get(url, payload);
          if (response?.success) {
            setIsPoNumberOpen(false);
            setSelectedEstimate(null);

            const isAccepting = status === "accept";
            swal({
              icon: "success",
              title: isAccepting ? "Estimate Accepted!" : "Estimate Rejected!",
              text: isAccepting
                ? "The estimate has been accepted successfully and moved to the Accepted tab.\n Click below to view the details."
                : "The estimate has been rejected successfully and moved to the Rejected tab.\n Click below to view the details.",
              buttons: {
                cancel: {
                  text: "Close",
                  value: false,
                  visible: true,
                },
                confirm: {
                  text: "View Details",
                  value: true,
                },
              },
            }).then((viewDetails) => {
              if (viewDetails) {
                history(`/estimate/detail/${response?.data?._id || quoteId}`);
              }
            });
            await fetchAllCounts();
            fetchEstimates();
          } else {
            toast.error(response?.message || `Failed to ${status} the quote`);
          }
        } catch (error) {
          console.error(`Error ${status}ing quote:`, error);
          toast.error(`Error ${status}ing quote. Please try again.`);
        } finally {
          loader(false);
          setActionLoading(null);
        }
      }
    });
  };

  // ── Negotiation message generator ─────────────────────────────────────────
  const generateNegotiationMessage = (
    estimate,
    originalProducts,
    updatedProducts,
  ) => {
    const docNumber =
      estimate.docNumber ||
      estimate.doc_number ||
      estimate.estimate_no ||
      raw.DocNumber ||
      `EST-${id?.slice(-6)}`;
    const customFields = estimate.CustomField || [];
    let displayDocNumber = docNumber;
    const topsField = customFields.find(
      (f) => f.Name === "Tops Shipping Systems, Corp",
    );
    if (
      topsField &&
      topsField.StringValue &&
      topsField.StringValue !== "ORD - null"
    ) {
      displayDocNumber = topsField.StringValue.replace("ORD - ", "");
    }
    let message = `I would like to negotiate estimate #${displayDocNumber}.\n\n`;
    const changes = [];

    updatedProducts.forEach((updated) => {
      const original = originalProducts.find(
        (p) => p.productId === updated.productId,
      );
      if (!original) {
        changes.push(
          `Product: ${updated.productName}\nQuantity: ${updated.quantity}\nUnit Price: ${formatCurrency(updated.unitPrice)}\nTotal: ${formatCurrency(updated.quantity * updated.unitPrice)}, \n`,
        );
        return;
      }
      const productChanges = [];
      if (original.productName !== updated.productName)
        productChanges.push(
          `Product: previous - ${original.productName} now - ${updated.productName}`,
        );
      if (original.quantity !== updated.quantity)
        productChanges.push(
          `Quantity: previous - ${original.quantity} now - ${updated.quantity}`,
        );
      if (original.unitPrice !== updated.unitPrice)
        productChanges.push(
          `Unit Price: previous - ${formatCurrency(original.unitPrice)} now - ${formatCurrency(updated.unitPrice)}`,
        );
      const origTotal = original.quantity * original.unitPrice;
      const updTotal = updated.quantity * updated.unitPrice;
      if (origTotal !== updTotal)
        productChanges.push(
          `Total: previous - ${formatCurrency(origTotal)} now - ${formatCurrency(updTotal)}`,
        );
      if ((original.description || "") !== (updated.description || ""))
        productChanges.push(
          `Description: previous - ${original.description || "-"} now - ${updated.description || "-"}`,
        );
      if (productChanges.length > 0)
        changes.push(`Updated Product:\n${productChanges.join("\n")}`);
    });

    message +=
      changes.length === 0
        ? "No changes have been made."
        : changes.join("\n\n");
    return message;
  };

  useEffect(() => {
    if (!selectedEstimate || !negotiationData) return;
    const message = generateNegotiationMessage(
      selectedEstimate,
      negotiationData.originalProducts || [],
      negotiationData.selectedProducts || [],
    );
    setNegotiationData((prev) => ({ ...prev, message }));
  }, [negotiationData?.selectedProducts]);

  // ── Open negotiation modal ────────────────────────────────────────────────
  const openNegotiationModal = async (estimate) => {
    setSelectedEstimate(estimate);
    await fetchAvailableProducts();

    const autoPopulatedProducts = estimate.lineItems.map((line, index) => {
      return {
        id: line.id || Date.now() + index,
        productId: line.productId || "",
        productName: line.itemName || "Product",
        quantity: line.quantity || 1,
        unitPrice: line.unitPrice || 0,
        description: line.description || "",
        amount: line.amount || 0,
      };
    });

    const validProducts = autoPopulatedProducts.filter(
      (product) => product.productName !== "Product" || product.unitPrice > 0,
    );

    setNegotiationData({
      products: validProducts,
      message: "",
      selectedProducts:
        validProducts.length > 0
          ? validProducts
          : [
              {
                id: Date.now(),
                productId: "",
                productName: "",
                quantity: 1,
                unitPrice: 0,
                description: "",
                amount: 0,
              },
            ],
    });
    setIsNegotiationModalOpen(true);
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const formatCurrency = (val) => {
    const num = Number(val ?? 0);
    const truncated = Math.trunc(num * 100) / 100;

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(truncated);
  };

  const formatDate = (str) => {
    if (!str) return "—";
    const d = new Date(str);
    if (isNaN(d)) return "—";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getInitials = (name = "") => {
    const parts = name.trim().split(" ").filter(Boolean);
    if (!parts.length) return "?";
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const statusColor = (status) => {
    const s = String(status || "").toUpperCase();
    const map = {
      ACCEPTED: { text: "text-[#008236]", dot: "bg-[#0a7822a6]" },
      PENDING: { text: "text-amber-700", dot: "bg-amber-500" },
      NEGOTIATE: { text: "text-blue-700", dot: "bg-blue-500" },
      REJECTED: { text: "text-red-600", dot: "bg-red-500" },
      CLOSED: { text: "text-gray-500", dot: "bg-gray-400" },
      CONVERTED: { text: "text-[#115B9D]", dot: "bg-[#115B9D]" },
      DRAFT: { text: "text-gray-500", dot: "bg-gray-400" },
      SENT: { text: "text-blue-600", dot: "bg-blue-500" },
    };
    return map[s] || { text: "text-gray-500", dot: "bg-gray-400" };
  };

  const StatusBadge = ({ status }) => {
    const c = statusColor(status);
    return (
      <span
        className={`inline-flex items-center gap-1.5 text-sm font-medium ${c.text}`}
      >
        <span className={`size-1.5 rounded-full ${c.dot}`} />
        {status || "—"}
      </span>
    );
  };

  // ── Tab label helper ──────────────────────────────────────────────────────
  const tabEmptyLabel = () => {
    if (activeTab === "synced") return "No accepted estimates found";
    if (activeTab === "pending") return "No pending quotes found";
    if (activeTab === "negotiation") return "No negotiation quotes found";
    return "No rejected quotes found";
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="container mx-auto">
        {/* ─── Summary Cards ─── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
          {[
            {
              label:
                activeTab === "synced"
                  ? "Total Estimates"
                  : activeTab === "pending"
                    ? "Total Pending Quotes"
                    : activeTab === "negotiation"
                      ? "Total Negotiation Quotes"
                      : "Total Rejected Quotes",
              value: total,
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-[20px] lg:w-[30px]"
                  viewBox="0 0 38 38"
                  fill="none"
                >
                  <path
                    d="M33.1465 9.23222L24.2404 0.326191C24.0326 0.11823 23.7488 0 23.4531 0H7.86719C6.0256 0 4.52734 1.49825 4.52734 3.33984V34.6602C4.52734 36.5017 6.0256 38 7.86719 38H30.1328C31.9744 38 33.4727 36.5017 33.4727 34.6602V10.0195C33.4727 9.71598 33.3452 9.4309 33.1465 9.23222ZM24.5664 3.80096L29.6717 8.90625H25.6797C25.0658 8.90625 24.5664 8.40683 24.5664 7.79297V3.80096ZM30.1328 35.7734H7.86719C7.25332 35.7734 6.75391 35.274 6.75391 34.6602V3.33984C6.75391 2.72598 7.25332 2.22656 7.86719 2.22656H22.3398V7.79297C22.3398 9.63456 23.8381 11.1328 25.6797 11.1328H31.2461V34.6602C31.2461 35.274 30.7467 35.7734 30.1328 35.7734Z"
                    fill="#115B9D"
                  />
                  <path
                    d="M25.6797 15.7344H12.3203C11.7055 15.7344 11.207 16.2328 11.207 16.8477C11.207 17.4625 11.7055 17.9609 12.3203 17.9609H25.6797C26.2945 17.9609 26.793 17.4625 26.793 16.8477C26.793 16.2328 26.2945 15.7344 25.6797 15.7344Z"
                    fill="#115B9D"
                  />
                  <path
                    d="M25.6797 20.1875H12.3203C11.7055 20.1875 11.207 20.686 11.207 21.3008C11.207 21.9156 11.7055 22.4141 12.3203 22.4141H25.6797C26.2945 22.4141 26.793 21.9156 26.793 21.3008C26.793 20.686 26.2945 20.1875 25.6797 20.1875Z"
                    fill="#115B9D"
                  />
                  <path
                    d="M25.6797 24.6406H12.3203C11.7055 24.6406 11.207 25.1391 11.207 25.7539C11.207 26.3687 11.7055 26.8672 12.3203 26.8672H25.6797C26.2945 26.8672 26.793 26.3687 26.793 25.7539C26.793 25.1391 26.2945 24.6406 25.6797 24.6406Z"
                    fill="#115B9D"
                  />
                  <path
                    d="M21.2266 29.0938H12.3203C11.7055 29.0938 11.207 29.5922 11.207 30.207C11.207 30.8219 11.7055 31.3203 12.3203 31.3203H21.2266C21.8414 31.3203 22.3398 30.8219 22.3398 30.207C22.3398 29.5922 21.8414 29.0938 21.2266 29.0938Z"
                    fill="#115B9D"
                  />
                </svg>
              ),
              bg:
                activeTab === "negotiation"
                  ? "bg-blue-50"
                  : activeTab === "rejected"
                    ? "bg-red-50"
                    : "bg-[#115B9D17]",
            },
            {
              label: "Total Amount",
              value: formatCurrency(totalAmt),
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-[20px] lg:w-[30px]"
                  viewBox="0 0 39 39"
                  fill="none"
                >
                  <path
                    d="M19.5 1.21875C9.41798 1.21875 1.21875 9.41798 1.21875 19.5C1.21875 29.582 9.41798 37.7812 19.5 37.7812C29.582 37.7812 37.7812 29.582 37.7812 19.5C37.7812 9.41798 29.582 1.21875 19.5 1.21875ZM19.5 3.65625C28.2647 3.65625 35.3533 10.7353 35.3533 19.5C35.3533 28.2647 28.2647 35.3533 19.5 35.3533C10.7353 35.3533 3.65625 28.2647 3.65625 19.5C3.65625 10.7353 10.7353 3.65625 19.5 3.65625ZM19.4833 9.75714C19.1614 9.76281 18.8547 9.89567 18.6304 10.1267C18.406 10.3577 18.2822 10.6682 18.286 10.9902V12.0161C16.3182 12.3853 14.6345 13.6525 14.6345 15.8485C14.6345 17.3719 15.3213 18.6041 16.2437 19.3215C17.166 20.0389 18.2075 20.3569 19.1215 20.6616C20.0356 20.9663 20.8246 21.2576 21.2734 21.6066C21.7221 21.9556 21.9375 22.2493 21.9375 23.1634C21.9375 25.1946 17.072 25.1946 17.072 23.1634C17.0756 23.0011 17.0468 22.8397 16.9871 22.6886C16.9275 22.5376 16.8383 22.4 16.7248 22.284C16.6112 22.1679 16.4757 22.0757 16.326 22.0127C16.1764 21.9497 16.0156 21.9173 15.8533 21.9173C15.6909 21.9173 15.5302 21.9497 15.3805 22.0127C15.2309 22.0757 15.0953 22.1679 14.9818 22.284C14.8682 22.4 14.779 22.5376 14.7194 22.6886C14.6598 22.8397 14.6309 23.0011 14.6345 23.1634C14.6345 25.3577 16.3196 26.6273 18.286 26.9982V28.0193C18.2824 28.1817 18.3113 28.3431 18.3709 28.4941C18.4305 28.6451 18.5197 28.7827 18.6333 28.8988C18.7468 29.0148 18.8824 29.1071 19.032 29.17C19.1817 29.233 19.3424 29.2654 19.5048 29.2654C19.6671 29.2654 19.8278 29.233 19.9775 29.17C20.1272 29.1071 20.2627 29.0148 20.3763 28.8988C20.4898 28.7827 20.579 28.6451 20.6386 28.4941C20.6982 28.3431 20.7271 28.1817 20.7235 28.0193V26.9982C22.6902 26.6275 24.3774 25.358 24.3774 23.1634C24.3774 21.64 23.6882 20.4078 22.7659 19.6904C21.8435 18.973 20.8021 18.6526 19.888 18.3479C18.9739 18.0432 18.1849 17.7543 17.7361 17.4053C17.2874 17.0563 17.072 16.7626 17.072 15.8485C17.072 13.8173 21.9375 13.8173 21.9375 15.8485C21.9375 16.1721 22.066 16.4824 22.2948 16.7111C22.5236 16.9399 22.8339 17.0684 23.1574 17.0684C23.481 17.0684 23.7913 16.9399 24.0201 16.7111C24.2488 16.4824 24.3774 16.1721 24.3774 15.8485C24.3774 13.6558 22.6896 12.3898 20.7235 12.0185V10.9902C20.7254 10.8271 20.6946 10.6653 20.6329 10.5144C20.5711 10.3635 20.4797 10.2264 20.3641 10.1115C20.2485 9.99649 20.1109 9.90589 19.9596 9.84503C19.8083 9.78417 19.6464 9.75428 19.4833 9.75714Z"
                    fill="#138C45"
                  />
                </svg>
              ),
              bg: "bg-[#f0fdf4]",
            },
            {
              label: "Total Tax",
              value: formatCurrency(totalTaxAmt),
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-[20px] lg:w-[30px]"
                  viewBox="0 0 39 39"
                  fill="none"
                >
                  <g clipPath="url(#clip0_159_495)">
                    <path
                      d="M4.88098 33.6369C5.67772 36.8087 8.54037 39.0242 11.8106 39H27.1896C30.4603 39.024 33.3231 36.8079 34.1193 33.6356C35.574 27.6731 31.9002 20.1806 27.7376 14.8116C28.5145 14.546 29.0817 13.8736 29.2124 13.0631C30.4222 13.2452 31.4442 14.0559 31.8969 15.1925C32.0308 15.5256 32.4094 15.6871 32.7426 15.5532C33.0757 15.4193 33.2372 15.0407 33.1033 14.7076C32.5103 13.2124 31.1941 12.1243 29.6141 11.8229C30.9684 10.9244 31.8021 9.42445 31.8501 7.8C31.8501 7.44102 31.5591 7.15 31.2001 7.15C30.8411 7.15 30.5501 7.44102 30.5501 7.8C30.474 9.15046 29.7056 10.3657 28.5182 11.0136C28.1004 10.6207 27.5487 10.4013 26.9751 10.4H26.5344L28.5442 7.2449C29.0538 6.44343 29.0858 5.42791 28.6277 4.59595C28.1696 3.764 27.2944 3.24801 26.3446 3.25H25.59C25.7926 2.47167 25.623 1.64341 25.1308 1.00734C24.6386 0.371276 23.8794-0.000758163 23.0751 1.16008e-06H15.9251C15.1209-0.000758163 14.3617 0.371276 13.8695 1.00734C13.3773 1.64341 13.2077 2.47167 13.4103 3.25H12.6556C11.7058 3.24809 10.8305 3.76427 10.3726 4.5964C9.91462 5.42852 9.94687 6.44415 10.4567 7.24555L12.4658 10.4H12.0251C10.919 10.398 9.97253 11.1937 9.78438 12.2837C9.59623 13.3737 10.2212 14.4406 11.264 14.8096C7.10073 20.1799 3.42563 27.6725 4.88098 33.6369ZM11.511 5.226C11.7381 4.80622 12.1784 4.54617 12.6556 4.55H13.9212L15.6502 8.70025C15.7396 8.91459 15.9366 9.06496 16.1669 9.0947C16.3972 9.12444 16.6259 9.02905 16.7668 8.84445C16.9078 8.65985 16.9395 8.41409 16.8501 8.19975L14.7252 3.09985C14.5581 2.69866 14.6026 2.2405 14.8436 1.87889C15.0847 1.51728 15.4905 1.30006 15.9251 1.3H23.0751C23.5097 1.30006 23.9156 1.51728 24.1566 1.87889C24.3977 2.2405 24.4421 2.69866 24.275 3.09985L22.1502 8.19975C22.012 8.53109 22.1685 8.91174 22.4999 9.04995C22.8312 9.18816 23.2119 9.0316 23.3501 8.70025L25.0791 4.55H26.3446C26.8204 4.54709 27.2598 4.80435 27.4901 5.22069C27.7204 5.63702 27.7049 6.14593 27.4496 6.54745L24.9933 10.4H14.0083L11.5545 6.5481C11.2965 6.14942 11.2797 5.6408 11.511 5.226ZM12.811 14.95H14.9501C15.3091 14.95 15.6001 14.659 15.6001 14.3C15.6001 13.941 15.3091 13.65 14.9501 13.65H12.0251C11.4867 13.65 11.0501 13.2135 11.0501 12.675C11.0501 12.1365 11.4867 11.7 12.0251 11.7H26.9751C27.5136 11.7 27.9501 12.1365 27.9501 12.675C27.9501 13.2135 27.5136 13.65 26.9751 13.65H18.8501C18.4911 13.65 18.2001 13.941 18.2001 14.3C18.2001 14.659 18.4911 14.95 18.8501 14.95H26.1873C30.3675 20.1097 34.2623 27.5685 32.857 33.3262C32.2022 35.9164 29.8612 37.723 27.1896 37.7H11.8106C9.13943 37.7236 6.79854 35.9173 6.14393 33.3274C4.73863 27.5685 8.63278 20.1097 12.8129 14.95H12.811Z"
                      fill="#E5A150"
                    />
                    <path
                      d="M9.74961 26.6501H11.6996V31.8501C11.6996 32.2091 11.9906 32.5001 12.3496 32.5001C12.7086 32.5001 12.9996 32.2091 12.9996 31.8501V26.6501H14.9496C15.3086 26.6501 15.5996 26.3591 15.5996 26.0001C15.5996 25.6411 15.3086 25.3501 14.9496 25.3501H9.74961C9.39062 25.3501 9.09961 25.6411 9.09961 26.0001C9.09961 26.3591 9.39062 26.6501 9.74961 26.6501Z"
                      fill="#E5A150"
                    />
                    <path
                      d="M24.3402 32.3907C24.4836 32.4864 24.6592 32.5212 24.8283 32.4875C24.9973 32.4537 25.1461 32.3541 25.2417 32.2107L26.6509 30.0949L28.0601 32.2087C28.2594 32.5074 28.663 32.588 28.9617 32.3888C29.2604 32.1896 29.341 31.7859 29.1417 31.4872L27.4309 28.9249L29.1404 26.3607C29.2693 26.1675 29.2853 25.9202 29.1825 25.712C29.0796 25.5037 28.8734 25.3662 28.6417 25.3512C28.4099 25.3362 28.1877 25.446 28.0588 25.6392L26.6509 27.7549L25.2417 25.6411C25.0425 25.3425 24.6389 25.2619 24.3402 25.4611C24.0415 25.6603 23.9609 26.064 24.1601 26.3626L25.8709 28.9249L24.1614 31.4892C23.9619 31.7875 24.0419 32.1911 24.3402 32.3907Z"
                      fill="#E5A150"
                    />
                    <path
                      d="M16.6362 32.4441C16.7938 32.5141 16.9727 32.5186 17.1336 32.4567C17.2945 32.3948 17.4242 32.2714 17.4942 32.1139L18.189 30.55H20.8112L21.506 32.1139C21.6003 32.3261 21.8006 32.4719 22.0316 32.4964C22.2626 32.5209 22.489 32.4203 22.6257 32.2325C22.7624 32.0447 22.7885 31.7983 22.6942 31.5861L21.8362 29.6562L21.8277 29.6374L20.0942 25.7374C19.9899 25.5025 19.7571 25.3511 19.5001 25.3511C19.2431 25.3511 19.0103 25.5025 18.906 25.7374L17.1724 29.6374L17.164 29.6562L16.306 31.5861C16.236 31.7436 16.2314 31.9225 16.2934 32.0835C16.3553 32.2444 16.4786 32.3741 16.6362 32.4441ZM19.5001 27.6003L20.2333 29.25H18.7669L19.5001 27.6003Z"
                      fill="#E5A150"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_159_495">
                      <rect width="39" height="39" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              ),
              bg: "bg-amber-50",
            },
          ].map((card) => (
            <div
              key={card.label}
              className={`${card.label == "Total Tax" ? "max-sm:col-span-2 max-sm:!flex-nowrap" : ""} border border-gray-200 rounded-2xl bg-white shadow-md px-4 sm:px-5 py-4 flex max-lg:flex-wrap sm:items-center gap-2 sm:gap-4`}
            >
              <div
                className={`min-w-[40px] min-w-[40px] lg:w-[70px] w-[40px] h-[40px] lg:h-[70px] rounded-xl ${card.bg} flex items-center justify-center shrink-0`}
              >
                {card.icon}
              </div>
              <div className="sm:space-y-1 max-lg:w-full">
                <p className="text-lg lg:text-2xl font-semibold text-[#3A3A3A]">
                  {card.value}
                </p>
                <p className="max-lg:text-sm text-md text-gray-600 font-medium">
                  {card.label}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div>
          {/* ─── Tabs + Search Row ─── */}
          <div className="flex flex-col gap-3 mb-4">
            <div className="flex flex-col lg:flex-row items-start sm:items-center justify-between gap-3">
              {/* Main Tabs */}
              <div className="w-full lg:w-fit max-lg:overflow-auto flex items-center gap-2 bg-[#ECF2F7] rounded-full max-lg:[scrollbar-width:none] max-lg:[-ms-overflow-style:none] max-lg:[&::-webkit-scrollbar]:hidden p-2">
                {[
                  { key: "pending", label: "Pending Quotes" },
                  { key: "negotiate", label: "Negotiation" },
                  { key: "rejected", label: "Rejected" },
                  { key: "synced", label: "Accepted" },
                  { key: "closed", label: "Closed" },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => switchTab(key)}
                    className={`whitespace-nowrap px-3 py-2 rounded-full max-xl:text-sm text-md font-medium transition-all flex items-center gap-2 ${
                      activeTab === key
                        ? "bg-[#115B9D] text-white"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {label}
                    <span
                      className={`text-[10px] sm:text-xs font-semibold px-[2px] min-w-[21px] sm:min-w-[25px] h-[21px] sm:h-[25px] flex items-center justify-center rounded-full ${
                        activeTab === key
                          ? "bg-white text-[#115B9D]"
                          : "bg-[#D8E4EF] text-[#7C7C7C]"
                      }`}
                    >
                      {tabCounts[key]}
                    </span>
                  </button>
                ))}
              </div>

              {/* Search */}
              <div className="relative w-full">
                <svg
                  className="absolute left-5 top-[28px] -translate-y-1/2 text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
                <input
                  type="text"
                  placeholder="Search by customer, doc #, ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  className="w-full border border-gray-200 rounded-full pl-11 pr-12 py-4 text-md text-gray-800 outline-none bg-[#F8F8F8] border-[#DADADA] transition-all"
                />
                <div
                  className="absolute top-[10px] right-[8px] w-[38px] h-[38px] rounded-full flex justify-center items-center bg-[#115B9D] cursor-pointer hover:opacity-80"
                  onClick={handleSearch}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <g clipPath="url(#clip0_163_465)">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M7.29936 14.6456C5.35442 14.6456 3.51494 13.884 2.14411 12.5015C0.76157 11.1189 0 9.29116 0 7.34623C0 5.38958 0.76157 3.56181 2.14411 2.17927C3.51494 0.796729 5.35442 0.046875 7.29936 0.046875C9.24429 0.046875 11.0838 0.796729 12.4663 2.17927C13.8371 3.56181 14.5987 5.38958 14.5987 7.34623C14.5987 9.29116 13.8371 11.1189 12.4663 12.5015C11.0838 13.884 9.24429 14.6456 7.29936 14.6456ZM7.29936 13.0053C5.78793 13.0053 4.35852 12.4195 3.29233 11.3533C2.22613 10.2753 1.62859 8.85765 1.62859 7.34623C1.62859 5.82309 2.22613 4.4054 3.29233 3.32748C4.35852 2.26129 5.78793 1.67546 7.29936 1.67546C8.81078 1.67546 10.2402 2.26129 11.3064 3.32748C13.5208 5.5419 13.5208 9.13885 11.3064 11.3533C10.2402 12.4195 8.81078 13.0053 7.29936 13.0053Z"
                        fill="white"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M18.4776 19.9531C18.0792 19.9531 17.6926 19.8008 17.4114 19.5079L14.1073 16.2156C13.8261 15.9344 13.6738 15.5477 13.6738 15.1494C13.6738 14.7393 13.8261 14.3526 14.1073 14.0714C14.4002 13.7903 14.7752 13.6262 15.1853 13.6262C15.5953 13.6262 15.9703 13.7903 16.2632 14.0714L19.5555 17.3638C20.1413 17.9613 20.1413 18.9221 19.5555 19.5079C19.2626 19.8008 18.8877 19.9531 18.4776 19.9531Z"
                        fill="white"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_163_465">
                        <rect width="20" height="20" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div className="sm:hidden space-y-4">
            {filteredEstimates.length === 0 ? (
              <div className="max-w-md bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4 text-center py-16">
                <div className="flex flex-col items-center gap-3">
                  <div className="size-14 rounded-full bg-gray-100 flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-gray-400"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                  </div>

                  <p className="text-sm text-gray-500">{tabEmptyLabel()}</p>
                </div>
              </div>
            ) : (
              filteredEstimates.map((est) => (
                <div
                  key={est.id}
                  className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4"
                >
                  {/* HEADER */}
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Doc #</p>
                      <p className="text-sm font-semibold text-[#115B9D]">
                        {est.displayDocNumber}
                      </p>
                    </div>

                    {/* VIEW BUTTON */}
                    <button
                      onClick={() =>
                        history(`/estimate/detail/${est.id || est._id}`)
                      }
                      className="size-9 rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-[#115B9D] flex items-center justify-center transition"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      >
                        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </button>
                  </div>

                  {/* STATUS */}
                  <div className="grid grid-cols-2 gap-3 border-t border-b border-gray-100 py-3">
                    <div>
                      <p className="text-xs text-gray-500">Status</p>
                      <StatusBadge status={est.status} />
                    </div>

                    {/* DATE */}
                    <div className="">
                      <p className="text-xs text-gray-500">Transaction Date</p>
                      <p className="text-sm text-gray-700">
                        {formatDate(est.txnDate)}
                      </p>
                    </div>
                  </div>

                  {/* TAX + TOTAL */}
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <div>
                      <p className="text-xs text-gray-500">Total Tax</p>
                      <p className="text-sm text-gray-700">
                        {formatCurrency(est.tax, est.currencyCode)}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500">Total Amount</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {formatCurrency(est.total, est.currencyCode)}
                      </p>
                    </div>
                  </div>

                  {/* ACTION BUTTONS */}
                  <div className="flex gap-2 pt-2 flex-wrap">
                    {/* ACCEPT */}
                    {activeTab === "pending" && est.status === "PENDING" && (
                      <button
                        onClick={() => {
                          setIsPoNumberOpen(true);
                          setSelectedEstimate(est);
                          setPoNumber("");
                        }}
                        className="flex-1 py-2 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 text-sm font-semibold transition-all active:scale-[0.98]"
                      >
                        Accept
                      </button>
                    )}

                    {/* REJECT */}
                    {activeTab === "pending" && est.status === "PENDING" && (
                      <button
                        onClick={() =>
                          updateQuoteStatus(
                            est.mainRequestId,
                            "reject",
                            est,
                            est.type,
                          )
                        }
                        className="flex-1 py-2 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 text-sm font-semibold transition-all active:scale-[0.98]"
                      >
                        Reject
                      </button>
                    )}

                    {/* COUNTER OFFER */}
                    {activeTab === "pending" && !est.isQB && (
                      <button
                        onClick={() => openNegotiationModal(est)}
                        className="flex-1 py-2 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 text-sm font-semibold transition-all active:scale-[0.98]"
                      >
                        Counter
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* ─── Table ─── */}
          <div className="max-sm:!hidden">
            <div className="overflow-auto border border-gray-200 rounded-xl">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {[
                      "Actions",
                      "Doc #",
                      "Txn Date",
                      "Status",
                      // "Sub Total",
                      "Total Tax",
                      // "Discount",
                      "Total Amount",
                    ].map((h) => (
                      <th
                        key={h}
                        className="text-left py-3.5 px-4 font-semibold text-sm text-gray-800"
                      >
                        <span className="text-[13px] lg:text-sm text-gray-800 font-semibold uppercase whitespace-nowrap">
                          {h}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredEstimates.length === 0 ? (
                    <tr className="hover:bg-gray-50/50 transition-colors">
                      <td colSpan={8} className="py-3.5 px-4">
                        <div className="flex flex-col items-center gap-3">
                          <div className="size-14 rounded-full bg-gray-100 flex items-center justify-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="text-gray-400"
                            >
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <polyline points="14 2 14 8 20 8" />
                            </svg>
                          </div>
                          <p className="text-sm text-gray-500">
                            {tabEmptyLabel()}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredEstimates.map((est, index) => (
                      <tr
                        key={est.id || index}
                        className="hover:bg-gray-50/60 transition-colors"
                      >
                        {/* Actions */}
                        <td className="py-3.5 px-4">
                          <div className="flex gap-1">
                            {/* View */}
                            <button
                              type="button"
                              onClick={() =>
                                history(`/estimate/detail/${est.id || est._id}`)
                              }
                              title="View Details"
                              className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-[#115B9D] transition-colors"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            </button>

                            {/* Accept / Reject (only for Pending) */}
                            {activeTab === "pending" &&
                              est.status === "PENDING" && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setIsPoNumberOpen(true);
                                      setSelectedEstimate(est);
                                      setPoNumber("");
                                    }}
                                    disabled={actionLoading === est.id}
                                    title="Accept Quote"
                                    className={`size-8 rounded-lg hover:bg-[#f0fdf4] flex items-center justify-center transition-colors ${
                                      actionLoading === est.id
                                        ? "opacity-50 cursor-not-allowed"
                                        : "text-gray-400 hover:text-[#008236]"
                                    }`}
                                  >
                                    {actionLoading === est.id ? (
                                      <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <polyline points="20 6 9 17 4 12" />
                                      </svg>
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      updateQuoteStatus(
                                        est.mainRequestId,
                                        "reject",
                                        est,
                                        est.type,
                                      )
                                    }
                                    disabled={actionLoading === est.id}
                                    title="Reject Quote"
                                    className={`size-8 rounded-lg hover:bg-red-50 flex items-center justify-center transition-colors ${
                                      actionLoading === est.id
                                        ? "opacity-50 cursor-not-allowed"
                                        : "text-gray-400 hover:text-red-600"
                                    }`}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="18"
                                      height="18"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <line x1="18" y1="6" x2="6" y2="18" />
                                      <line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                  </button>
                                </>
                              )}

                            {/* Counter Offer (only for Pending tab) */}
                            {activeTab === "pending" && !est.isQB && (
                              <button
                                type="button"
                                onClick={() => openNegotiationModal(est)}
                                disabled={actionLoading === est.id}
                                title="Send Counter Offer"
                                className={`size-8 rounded-lg hover:bg-blue-50 flex items-center justify-center transition-colors ${
                                  actionLoading === est.id
                                    ? "opacity-50 cursor-not-allowed"
                                    : "text-gray-400 hover:text-blue-600"
                                }`}
                              >
                                {actionLoading === est.id ? (
                                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                                    <path d="M12 12v.01" />
                                    <path d="M12 9v3" />
                                  </svg>
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                        {/* Doc # */}
                        <td className="py-3.5 px-4">
                          <span className="inline-flex items-center max-lg:text-sm text-md font-semibold text-[#115B9D]">
                            {est.displayDocNumber}
                          </span>
                        </td>

                        {/* Txn Date */}
                        <td className="py-3.5 px-4">
                          <p className="max-lg:text-sm text-md text-[#686666] whitespace-nowrap">
                            {formatDate(est.txnDate)}
                          </p>
                        </td>

                        {/* Status */}
                        <td className="py-3.5 px-4 capitalize">
                          <StatusBadge status={est.status} />
                        </td>

                        {/* Total Tax */}
                        <td className="py-3.5 px-4">
                          <p className="max-lg:text-sm text-md text-[#686666] whitespace-nowrap">
                            {formatCurrency(est.tax, est.currencyCode)}
                          </p>
                        </td>

                        {/* Total Amount */}
                        <td className="py-3.5 px-4">
                          <p className="max-lg:text-sm text-md font-semibold text-[#7C7C7C] whitespace-nowrap">
                            {formatCurrency(est.total, est.currencyCode)}
                          </p>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ─── Pagination ─── */}
          {total > filters?.count && (
            <div className="mt-5 flex flex-col gap-4 border-t border-gray-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-600">
                  {PaginationTextView(filters.page, filters?.count, total)}
                </p>
              </div>
              <div className="custom-pagination">
                <Pagination
                  currentPage={filters.page}
                  totalSize={total}
                  sizePerPage={filters?.count}
                  changeCurrentPage={handlePageChange}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ─── PO Number Modal ─── */}
      <Transition appear show={isPoNumberOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[999]"
          onClose={() => setIsPoNumberOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl bg-white rounded-2xl shadow-xl flex flex-col max-h-[90vh]">
                  <div className="flex items-center justify-between px-6 py-5 border-b shrink-0">
                    <div className="flex items-center gap-3">
                      <div className="size-9 rounded-xl bg-[#115B9D17] flex items-center justify-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="17"
                          height="17"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-[#115B9D]"
                        >
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                        </svg>
                      </div>
                      <div>
                        <Dialog.Title className="text-base font-semibold text-gray-900" />
                        {selectedEstimate && (
                          <div className="mt-1">
                            <StatusBadge status={selectedEstimate.status} />
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setIsPoNumberOpen(false)}
                      className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-600 transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M18 6 6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="p-5">
                    <label className="text-[11px] text-gray-400 font-medium uppercase tracking-wide">
                      Enter PO Number{" "}
                      <span className="text-[9px]">
                        ( Skip if not applicable )
                      </span>
                    </label>
                    <input
                      type="text"
                      value={poNumber}
                      onChange={(e) => setPoNumber(e.target.value)}
                      placeholder="PO Number"
                      className="w-full mt-2 px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#115B9D]/20"
                    />
                  </div>
                  <div className="flex items-center justify-end gap-3 px-6 py-4 border-t shrink-0">
                    <button
                      type="button"
                      onClick={() =>
                        updateQuoteStatus(
                          selectedEstimate.mainRequestId,
                          "accept",
                          {
                            poNumber,
                          },
                          selectedEstimate?.type,
                        )
                      }
                      className="w-fit inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm px-4 py-2.5 rounded-xl transition-colors font-medium"
                    >
                      Accept
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsPoNumberOpen(false)}
                      className="px-5 py-2.5 text-sm text-gray-600 hover:opacity-80 rounded-xl transition-colors border border-gray-200 bg-white"
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <Transition appear show={isNegotiationModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[999]"
          onClose={() => setIsNegotiationModalOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-3xl bg-white rounded-2xl shadow-xl flex flex-col max-h-[90vh]">
                  <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 shrink-0">
                    <div className="flex items-center gap-3">
                      <div className="size-9 rounded-xl bg-blue-50 flex items-center justify-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="17"
                          height="17"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-blue-600"
                        >
                          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                          <path d="M12 22V12" />
                          <path d="M9 10.5l3-3 3 3" />
                        </svg>
                      </div>
                      <div>
                        <Dialog.Title className="text-base font-semibold text-gray-900">
                          Send Counter Offer
                        </Dialog.Title>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {selectedEstimate?.customerName}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setIsNegotiationModalOpen(false)}
                      className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-600 transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M18 6 6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5">
                    {/* Message */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Negotiation Message{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        rows="4"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                        placeholder="Write your counter offer message here..."
                        value={negotiationData.message}
                        onChange={(e) =>
                          setNegotiationData((prev) => ({
                            ...prev,
                            message: e.target.value,
                          }))
                        }
                      />
                    </div>

                    {/* Products */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="text-sm font-semibold text-gray-700">
                          Proposed Products{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <button
                          type="button"
                          onClick={addNegotiationProduct}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="16" />
                            <line x1="8" y1="12" x2="16" y2="12" />
                          </svg>
                          Add Product
                        </button>
                      </div>
                      <div className="space-y-3">
                        {negotiationData.selectedProducts.length === 0 && (
                          <div className="text-center py-8 bg-gray-50 rounded-xl border border-gray-200">
                            <p className="text-sm text-gray-500">
                              No products added yet. Click "Add Product" to
                              propose products.
                            </p>
                          </div>
                        )}
                        {negotiationData.selectedProducts.map(
                          (product, index) => (
                            <div
                              key={product.id}
                              className="border border-gray-200 rounded-xl p-4 bg-gray-50/30"
                            >
                              <div className="flex justify-end mb-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    removeNegotiationProduct(index)
                                  }
                                  className="text-red-400 hover:text-red-600"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                  </svg>
                                </button>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Product
                                  </label>
                                  <Select
                                    menuPortalTarget={document.body}
                                    styles={{
                                      menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                      }),
                                    }}
                                    value={
                                      availableProducts
                                        .map((p) => ({
                                          value: p.Id,
                                          label: p.Name,
                                        }))
                                        .find(
                                          (option) =>
                                            option.value === product.productId,
                                        ) || null
                                    }
                                    onChange={(selected) =>
                                      updateNegotiationProduct(
                                        index,
                                        "productId",
                                        selected?.value || "",
                                      )
                                    }
                                    options={availableProducts.map((p) => ({
                                      value: p.Id,
                                      label: p.Name,
                                    }))}
                                    placeholder="Select Product"
                                    // isClearable
                                    className="text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Quantity
                                  </label>
                                  <input
                                    type="number"
                                    min="1"
                                    onWheel={(e) => e.target.blur()}
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                                    value={product.quantity}
                                    onChange={(e) =>
                                      updateNegotiationProduct(
                                        index,
                                        "quantity",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Unit Price ($)
                                  </label>
                                  <input
                                    type="number"
                                    min="0"
                                    onWheel={(e) => e.target.blur()}
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                                    value={product.unitPrice}
                                    onChange={(e) =>
                                      updateNegotiationProduct(
                                        index,
                                        "unitPrice",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Description
                                  </label>
                                  <input
                                    type="text"
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                                    value={product.description}
                                    onChange={(e) =>
                                      updateNegotiationProduct(
                                        index,
                                        "description",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Optional description"
                                  />
                                </div>
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 shrink-0">
                    <button
                      type="button"
                      onClick={() => setIsNegotiationModalOpen(false)}
                      className="px-5 py-2.5 text-sm text-gray-600 hover:opacity-80 rounded-xl transition-colors border border-gray-200 bg-white"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={sendCounterOffer}
                      disabled={actionLoading === selectedEstimate?.id}
                      className={`w-fit inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm px-4 py-2.5 rounded-xl transition-colors font-medium ${actionLoading === selectedEstimate?.id ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      {actionLoading === selectedEstimate?.id
                        ? "Sending..."
                        : "Send Counter Offer"}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
